import { DateTime } from 'luxon';

import { defaultEntity, TaskInfoModel } from '@/entities/task/model/TaskInfoModel';
import { WeeklyWorkLog } from '@/types';
import {
  Timelog,
  TimelogsTask,
  TimelogsTaskFull,
  TimelogsUser,
  TimelogUserRef,
} from '@/types/timelog';
import {
  YandexTask,
  YandexTaskInfo,
  YandexTaskParent,
  YandexUser,
  YLog,
  YLogUser,
} from '@/types/yandex';

import { parseDuration } from './parseDuration';

// Экраны таймлога написаны на формах данных Трекера: v2 приводим к ним, а не наоборот

export const transformTimelogToWeeklyWorkLog = (log: Timelog): WeeklyWorkLog => ({
  id: log.id,
  taskKey: log.task.key,
  taskTitle: log.task.summary,
  duration: log.duration,
  durationMinutes: parseDuration(log.duration),
  comment: log.comment,
  // start приходит в UTC, день считаем по местному времени, как у логов Трекера
  date: DateTime.fromISO(log.start).toISODate()!,
  createdAt: log.createTime,
  start: DateTime.fromISO(log.start).toISO()!,
  calendarEvent: log.calendarEvent,
});

export const transformTimelogsTask = (task: TimelogsTask): YandexTask => ({
  key: task.key,
  summary: task.summary,
  status: { key: task.status.key, display: task.status.name },
  queue: { key: task.queue.key, display: task.queue.name },
  createdAt: task.createTime,
  updatedAt: task.updateTime,
});

const toEntity = (ref: { key: string; name: string }) => ({
  self: '',
  id: ref.key,
  key: ref.key,
  display: ref.name,
});

const toLogUser = (ref: TimelogUserRef | null): YLogUser => ({
  self: '',
  id: ref?.id ?? '',
  cloudUid: '',
  passportUid: 0,
  display: ref?.displayName ?? '',
});

export const transformTimelogsTaskInfo = (task: TimelogsTaskFull): TaskInfoModel =>
  new TaskInfoModel({
    id: task.id,
    key: task.key,
    summary: task.summary,
    description: task.description ?? '',
    createdAt: task.createTime,
    updatedAt: task.updateTime,
    createdBy: toLogUser(task.createdBy),
    updatedBy: toLogUser(task.updatedBy),
    assignee: toLogUser(task.assignee ?? task.createdBy),
    status: toEntity(task.status),
    previousStatus: defaultEntity,
    priority: toEntity(task.priority),
    queue: toEntity(task.queue),
    type: toEntity(task.type),
    parent: task.parent
      ? { self: '', id: task.parent.key, key: task.parent.key, display: task.parent.summary ?? '' }
      : defaultEntity,
    // Избранного задач в v2 нет
    favorite: false,
    estimation: parseDuration(task.estimation ?? ''),
    originalEstimation: parseDuration(task.originalEstimation ?? ''),
    spent: parseDuration(task.spent ?? ''),
    statusStartTime: task.statusChangeTime ?? '',
    statusType: defaultEntity,
  });

const toParent = (ref: { key: string; summary: string | null } | null) =>
  ref ? { self: '', id: ref.key, key: ref.key, display: ref.summary ?? ref.key } : undefined;

// Статистика «оценка и факт» написана на задачах и логах Трекера. Заполняем только то,
// что она читает: ключ, название, статус, исполнителя, оценки, родителя и эпик; автора лога,
// длительность и начало

export const transformTimelogsTaskForStats = (task: TimelogsTaskFull): YandexTaskInfo =>
  ({
    id: task.id,
    key: task.key,
    summary: task.summary,
    status: toEntity(task.status),
    assignee: task.assignee ? toLogUser(task.assignee) : undefined,
    estimation: task.estimation ?? undefined,
    originalEstimation: task.originalEstimation ?? undefined,
    parent: toParent(task.parent) as YandexTaskParent | undefined,
    epic: toParent(task.epic) as YandexTaskParent | undefined,
  }) as YandexTaskInfo;

export const transformTimelogForStats = (log: Timelog): YLog =>
  ({
    createdBy: toLogUser(log.user),
    duration: log.duration,
    start: log.start,
    comment: log.comment,
  }) as YLog;

export const transformTimelogsUser = (user: TimelogsUser): YandexUser => ({
  uid: user.id,
  display: user.displayName,
  email: user.email,
});
